import { useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { PageHeader, PageLayout } from '@/widgets/layout/ui/AppLayout'
import { Card, SkeletonStack } from '@/shared/ui'
import { formatDateTime } from '@/shared/lib/time'
import { useCall } from '@/entities/call'
import { ReportCard } from '@/entities/call/ui/ReportCard'
import { CallPlayer } from '@/entities/call/ui/CallPlayer'
import { SentimentIcon } from '@/entities/call/ui/OutcomeIcon'
import type { CallTurn, Highlight, TurnTrace } from '@/shared/api/types'
import { ClientBrief } from './ui/ClientBrief'
import styles from './CallPage.module.css'

/** Что показывать в ленте. */
type Mode = 'all' | 'marks'

/** Разговор целиком: показатели, запись, стенограмма. */
export function CallPage() {
  const { id } = useParams<{ id: string }>()
  const callId = Number(id)
  const call = useCall(Number.isFinite(callId) ? callId : null)
  const [searchParams] = useSearchParams()

  // Плеер и стенограмма связаны в обе стороны: воспроизведение
  // подсвечивает реплику, клик по реплике перематывает запись.
  // ?t=<мс> приходит из истории карточки клиента: ссылка ведёт на
  // реплику, где прозвучал факт, а не в начало записи.
  const [activeTurn, setActiveTurn] = useState<number | null>(null)
  const [seekTo, setSeekTo] = useState<number | null>(() => offsetParam(searchParams.get('t')))
  const [mode, setMode] = useState<Mode>('all')

  const marksByTurn = useMemo(() => {
    const map = new Map<number, Highlight[]>()
    for (const mark of call.data?.highlights ?? []) {
      const list = map.get(mark.turnIndex)
      if (list) list.push(mark)
      else map.set(mark.turnIndex, [mark])
    }
    return map
  }, [call.data?.highlights])

  if (call.isPending) {
    return (
      <>
        <PageHeader title="Разговор" />
        <Card>
          <SkeletonStack rows={5} height={40} />
        </Card>
      </>
    )
  }

  if (!call.data) {
    return (
      <>
        <PageHeader title="Разговор не найден" />
        <Card>
          <Link className={styles.link} to="/calls">
            Ко всем звонкам
          </Link>
        </Card>
      </>
    )
  }

  const { call: info, turns, highlights } = call.data

  // В режиме моментов показываем размеченные реплики и по одной вокруг:
  // фраза без контекста часто непонятна.
  const shown =
    mode === 'marks' && highlights.length > 0
      ? turns.filter((turn) =>
          highlights.some((mark) => Math.abs(mark.turnIndex - turn.index) <= 1),
        )
      : turns

  return (
    <PageLayout
      aside={
        <>
          {info.contactId ? (
            <Card title="Клиент">
              <ClientBrief contactId={info.contactId} />
            </Card>
          ) : null}
          <Card title="Итог разговора">
            <ReportCard call={info} />
          </Card>
        </>
      }
    >
      <PageHeader
        title="Разговор"
        description={`${formatDateTime(info.startedAt ?? info.createdAt)}${
          info.kind === 'test' ? ' · проверка из кабинета' : ''
        }`}
      />

      <div className={styles.stats}>
        <span className={styles.stat} title="длительность">
          <span className={styles.statIcon}>
            <ClockIcon />
          </span>
          {duration(info.durationMs)}
        </span>

        <span className={styles.stat} title="реплик в разговоре">
          <span className={styles.statIcon}>
            <TurnsIcon />
          </span>
          {info.turnsCount}
        </span>

        {info.billedMs !== null && info.billedMs !== undefined && (
          <span
            className={styles.stat}
            title={info.billingNote || 'списано с пакета минут, посекундно с момента ответа'}
          >
            списано {info.billedMs > 0 ? duration(info.billedMs) : '0'}
            {info.billingNote ? ` · ${info.billingNote}` : ''}
          </span>
        )}

        {info.bargeInCount > 0 && (
          <span className={styles.stat} title="перебиваний">
            <span className={styles.statIcon}>
              <CutIcon />
            </span>
            {info.bargeInCount}
          </span>
        )}

        {info.medianReplyMs > 0 && (
          <span
            className={`${styles.stat} ${info.medianReplyMs > 1500 ? styles.statWarn : ''}`}
            title="медиана времени ответа агента"
          >
            <span className={styles.statIcon}>
              <BoltIcon />
            </span>
            {(info.medianReplyMs / 1000).toFixed(1)} с
          </span>
        )}

        {info.report && (
          <span className={styles.stat} title="настроение собеседника">
            <span className={styles.statIcon}>
              <SentimentIcon sentiment={info.report.sentiment} />
            </span>
            {sentimentLabel(info.report.sentiment)}
          </span>
        )}

        {info.talkRatio > 0 && (
          <span className={styles.talk} title="кто сколько говорил">
            <span className={styles.talkBar}>
              <span className={styles.talkAgent} style={{ width: `${info.talkRatio * 100}%` }} />
              <span
                className={styles.talkHuman}
                style={{ width: `${(1 - info.talkRatio) * 100}%` }}
              />
            </span>
            <span className={styles.talkLabel}>агент {Math.round(info.talkRatio * 100)}%</span>
          </span>
        )}
      </div>

      {info.hasRecording && (
        <Card
          title="Запись"
          subtitle="Зелёным отмечено, что сработало, красным — где разговор просел. Клик по метке или реплике — переход к месту"
        >
          <CallPlayer
            call={info}
            turns={turns}
            highlights={highlights}
            onActiveTurn={setActiveTurn}
            seekTo={seekTo}
          />
        </Card>
      )}

      <TraceSummary turns={turns} />

      <Card
        title="Стенограмма"
        action={
          highlights.length > 0 ? (
            <div className={styles.modes}>
              <button
                type="button"
                className={`${styles.mode} ${mode === 'all' ? styles.modeActive : ''}`}
                onClick={() => setMode('all')}
              >
                Весь разговор
              </button>
              <button
                type="button"
                className={`${styles.mode} ${mode === 'marks' ? styles.modeActive : ''}`}
                onClick={() => setMode('marks')}
              >
                Только моменты
              </button>
            </div>
          ) : undefined
        }
      >
        {shown.length === 0 ? (
          <p className={styles.empty}>Никто не сказал ни слова</p>
        ) : (
          <div className={styles.feed}>
            {shown.map((turn) => (
              <TurnRow
                key={turn.index}
                turn={turn}
                marks={marksByTurn.get(turn.index) ?? []}
                active={turn.index === activeTurn}
                onSeek={info.hasRecording ? () => setSeekTo(turn.offsetMs / 1000) : undefined}
              />
            ))}
          </div>
        )}
      </Card>
    </PageLayout>
  )
}

interface TurnRowProps {
  turn: CallTurn
  marks: Highlight[]
  active: boolean
  /** Перемотать запись на эту реплику. undefined — записи нет. */
  onSeek?: () => void
}

function TurnRow({ turn, marks, active, onSeek }: TurnRowProps) {
  const human = turn.role === 'human'
  const heard = turn.interrupted && turn.heard ? turn.heard : turn.text
  const [open, setOpen] = useState(false)

  return (
    <div
      className={[styles.turn, onSeek ? styles.turnSeekable : '', active ? styles.turnActive : '']
        .filter(Boolean)
        .join(' ')}
      onClick={onSeek}
    >
      <span className={styles.time}>{offset(turn.offsetMs)}</span>
      <span className={`${styles.who} ${human ? styles.whoHuman : ''}`} />

      <span className={styles.text}>
        {heard}
        {turn.tail && <span className={styles.tail}> {turn.tail}</span>}

        {turn.interrupted && (
          <span className={styles.cut} title="здесь собеседник перебил">
            <CutIcon />
          </span>
        )}

        {marks.map((mark) => (
          <span
            key={mark.kind}
            className={`${styles.highlight} ${
              mark.good ? styles.highlightGood : styles.highlightBad
            }`}
            title={mark.note}
          >
            {mark.label}
          </span>
        ))}

        {!human && turn.replyMs > 0 && (
          <button
            type="button"
            className={`${styles.meta} ${turn.replyMs > 1500 ? styles.metaSlow : ''} ${turn.trace ? styles.metaClickable : ''}`}
            title={turn.trace ? 'через сколько агент ответил — нажми, чтобы увидеть, куда ушло время' : 'через сколько агент ответил'}
            onClick={(e) => {
              e.stopPropagation()
              if (turn.trace) setOpen((v) => !v)
            }}
          >
            <BoltIcon />
            {(turn.replyMs / 1000).toFixed(1)}
          </button>
        )}
        {open && turn.trace && <TraceBar trace={turn.trace} />}
      </span>
    </div>
  )
}

/* ---------- Трейс задержки ---------- */

const STAGES: { key: keyof TurnTrace; label: string; hint: string; cls: string }[] = [
  { key: 'sttMs', label: 'STT', hint: 'распознавание: конец речи → текст', cls: 'stStt' },
  { key: 'llmTtftMs', label: 'LLM', hint: 'модель: запрос → первый токен', cls: 'stLlm' },
  { key: 'chunkWaitMs', label: 'ждём текст', hint: 'первый токен → первый кусок в синтез (в режиме «целиком» — вся реплика)', cls: 'stChunk' },
  { key: 'enrichMs', label: 'теги', hint: 'расстановка аудио-тегов отдельной моделью', cls: 'stEnrich' },
  { key: 'ttsMs' as keyof TurnTrace, label: 'TTS', hint: 'синтез: текст → первый байт звука', cls: 'stTts' },
  { key: 'mediaMs', label: 'тракт', hint: 'первый байт → первый кадр в линию (+ растяжка темпа)', cls: 'stMedia' },
]

function stageMs(t: TurnTrace, key: keyof TurnTrace | 'ttsMs'): number {
  if (key === 'ttsMs') return t.ttsTtfbMs
  const v = t[key as keyof TurnTrace]
  return typeof v === 'number' ? v : 0
}

function shortModel(m: string): string {
  return m.replace(/^.*\//, '').replace('eleven_', '')
}

function TraceBar({ trace }: { trace: TurnTrace }) {
  const total = Math.max(trace.firstAudioMs, 1)
  const parts = STAGES.map((s) => ({ ...s, ms: stageMs(trace, s.key) })).filter((s) => s.ms > 0)
  return (
    <div className={styles.trace} onClick={(e) => e.stopPropagation()}>
      <div className={styles.traceBar} title={`до первого звука: ${trace.firstAudioMs} мс`}>
        {parts.map((s) => (
          <div
            key={s.key}
            className={`${styles.traceSeg} ${styles[s.cls]}`}
            style={{ flexGrow: s.ms, flexBasis: 0 }}
            title={`${s.label}: ${s.ms} мс — ${s.hint}`}
          >
            {s.ms / total > 0.12 && (
              <span className={styles.traceSegLabel}>
                {s.label} {s.ms}
              </span>
            )}
          </div>
        ))}
      </div>
      <div className={styles.traceLegend}>
        {parts.map((s) => (
          <span key={s.key} className={styles.traceLegendItem}>
            <i className={`${styles.traceDot} ${styles[s.cls]}`} />
            {s.label} <b>{s.ms}</b>
          </span>
        ))}
        <span className={styles.traceLegendItem}>
          = <b>{(trace.firstAudioMs / 1000).toFixed(2)} с</b> до звука
        </span>
      </div>
      <div className={styles.traceMeta}>
        {shortModel(trace.llmModel)} · {shortModel(trace.ttsModel)} · {trace.chunking}
        {trace.speed && trace.speed !== 1 ? ` · темп ×${trace.speed}` : ''} · {trace.replyChars} симв
        {trace.llmTotalMs > 0 && ` · модель целиком ${trace.llmTotalMs} мс`}
        {trace.spokenMs > 0 && ` · звучала ${(trace.spokenMs / 1000).toFixed(1)} с`}
      </div>
      {(trace.llmCostUsd || trace.ttsCredits) ? (
        <div className={styles.traceMeta}>
          Стоила:
          {trace.llmCostUsd ? ` модель ${usd(trace.llmCostUsd)} (${trace.promptTokens ?? 0}+${trace.completionTokens ?? 0} ток.)` : ''}
          {trace.llmCostUsd && trace.ttsCredits ? ' ·' : ''}
          {trace.ttsCredits ? ` синтез ${trace.ttsCredits} кр (${usd(creditsUsd(trace.ttsCredits))})` : ''}
        </div>
      ) : null}
    </div>
  )
}

/** Цена кредита ElevenLabs по нашему тарифу (Creator, добор $0.30 за 1000). */
const USD_PER_CREDIT = 0.3 / 1000
const creditsUsd = (n: number) => n * USD_PER_CREDIT
const usd = (v: number) => (v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(3)}`)

/** Сводка по звонку: медиана и худшая реплика по каждому этапу. */
function TraceSummary({ turns }: { turns: CallTurn[] }) {
  const traced = turns.filter((t) => t.trace && t.trace.firstAudioMs > 0).map((t) => t.trace as TurnTrace)
  if (traced.length < 1) return null

  const med = (xs: number[]): number => {
    const a = [...xs].sort((x, y) => x - y)
    return a.length ? (a[Math.floor(a.length / 2)] ?? 0) : 0
  }
  const rows = STAGES.map((s) => {
    const xs = traced.map((t) => stageMs(t, s.key))
    return { ...s, med: med(xs), max: Math.max(...xs) }
  }).filter((r) => r.max > 0)
  if (rows.length === 0) return null
  const llmUsd = traced.reduce((a, t) => a + (t.llmCostUsd ?? 0), 0)
  const credits = traced.reduce((a, t) => a + (t.ttsCredits ?? 0), 0)
  const promptTok = traced.reduce((a, t) => a + (t.promptTokens ?? 0), 0)
  const complTok = traced.reduce((a, t) => a + (t.completionTokens ?? 0), 0)
  const totals = traced.map((t) => t.firstAudioMs)
  const medTotal = Math.max(med(totals), 1)
  const first = traced[0]!
  const worst = rows.reduce((a, b) => (b.med > a.med ? b : a), rows[0]!)

  return (
    <Card title="Задержка ответа" subtitle={`${traced.length} реплик · медиана ${(medTotal / 1000).toFixed(2)} с · худшая ${(Math.max(...totals) / 1000).toFixed(2)} с`}>
      <div className={styles.traceBar}>
        {rows.map((r) => (
          <div
            key={r.key}
            className={`${styles.traceSeg} ${styles[r.cls]}`}
            style={{ flexGrow: r.med, flexBasis: 0 }}
            title={`${r.label}: медиана ${r.med} мс, худшая ${r.max} мс`}
          >
            {r.med / medTotal > 0.12 && (
              <span className={styles.traceSegLabel}>
                {r.label} {r.med}
              </span>
            )}
          </div>
        ))}
      </div>
      <table className={styles.traceTable}>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className={r.key === worst.key ? styles.traceWorst : ''}>
              <td>
                <i className={`${styles.traceDot} ${styles[r.cls]}`} /> {r.label}
              </td>
              <td className={styles.traceHint}>{r.hint}</td>
              <td className={styles.traceNum}>{r.med} мс</td>
              <td className={styles.traceNum}>худшая {r.max}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.traceMeta}>
        Тормозит в этом звонке: <b>{worst.label}</b>. Настройки: {shortModel(first.llmModel)} · {shortModel(first.ttsModel)} · {first.chunking}
        {first.speed && first.speed !== 1 ? ` · темп ×${first.speed}` : ''}
      </div>
      {(llmUsd > 0 || credits > 0) && (
        <div className={styles.traceMeta}>
          Звонок стоил: <b>{usd(llmUsd + creditsUsd(credits))}</b>
          {llmUsd > 0 && ` · модель ${usd(llmUsd)} (${promptTok.toLocaleString('ru')} + ${complTok.toLocaleString('ru')} ток.)`}
          {credits > 0 && ` · синтез ${credits.toLocaleString('ru')} кр (${usd(creditsUsd(credits))})`}
          {' '}· без телефонии
        </div>
      )}
    </Card>
  )
}

function sentimentLabel(sentiment: string): string {
  if (sentiment === 'positive') return 'доброжелательно'
  if (sentiment === 'negative') return 'раздражённо'
  return 'спокойно'
}

/** ?t= в миллисекундах → секунды для плеера. Мусор и минус — без перемотки. */
function offsetParam(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null
  const ms = Number(raw)
  if (!Number.isFinite(ms) || ms < 0) return null
  return ms / 1000
}

/** Смещение от начала записи — минуты и секунды. */
function offset(ms: number): string {
  const seconds = Math.floor(ms / 1000)
  const mm = Math.floor(seconds / 60)
  const ss = seconds % 60
  return `${mm}:${String(ss).padStart(2, '0')}`
}

function duration(ms: number): string {
  if (ms <= 0) return '—'
  const seconds = Math.round(ms / 1000)
  const mm = Math.floor(seconds / 60)
  const ss = seconds % 60
  return `${mm}:${String(ss).padStart(2, '0')}`
}

function ClockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="5.4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M7 4.2V7.2L9 8.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function TurnsIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M2 4.2H12M2 7H9M2 9.8H10.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  )
}

function CutIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M6 1.5V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M3 4L6 1.5L9 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function BoltIcon() {
  return (
    <svg width="9" height="12" viewBox="0 0 9 12" fill="currentColor" aria-hidden="true">
      <path d="M5.2 0L0 6.8h3L3.8 12L9 5.2H6L5.2 0Z" />
    </svg>
  )
}
